import React, { useMemo } from 'react';
import { css } from '@emotion/css';
import parse from 'html-react-parser';
import { IoInformationCircleOutline } from 'react-icons/io5';
import type { EventDetail } from '../../../../api/services/events/types';

const cleanHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) return '';
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(el => {
    const element = el as HTMLElement;
    
    if (element.style.backgroundColor === 'rgb(255, 255, 255)' || 
        element.style.backgroundColor === 'transparent') {
      element.style.removeProperty('background-color');
    }
    
    if (element.style.color === 'rgb(30, 41, 59)' || 
        element.style.color === 'rgb(65, 67, 70)') {
      element.style.removeProperty('color');
    }
  });
  
  const headings = tempDiv.querySelectorAll('h2');
  headings.forEach((heading, index) => {
    if (heading.textContent?.includes('Descrição')) {
      heading.classList.add('event-description-heading');
    } else if (heading.textContent?.includes('Informações')) {
      heading.classList.add('event-info-heading');
    } else if (heading.textContent?.includes('Programação')) {
      heading.classList.add('event-program-heading');
    }
    
    if (index === headings.length - 1) {
      heading.classList.add('last-heading');
    }
  });
  
  const paragraphs = tempDiv.querySelectorAll('p');
  paragraphs.forEach((para, index) => {
    const firstChar = para.textContent?.trim().charAt(0) || '';
    if (/[\u{1F300}-\u{1F6FF}]/u.test(firstChar)) {
      para.classList.add('emoji-paragraph');
    }
    
    if (para.innerHTML.includes('<strong>Data e hora:</strong>')) {
      para.classList.add('event-date-info');
    } else if (para.innerHTML.includes('<strong>Local:</strong>')) {
      para.classList.add('event-location-info');
    } else if (para.innerHTML.includes('<strong>Duração:</strong>')) {
      para.classList.add('event-duration-info');
    } else if (para.innerHTML.includes('<strong>Idade:</strong>')) {
      para.classList.add('event-age-info');
    } else if (para.innerHTML.includes('<strong>Acessibilidade:</strong>')) {
      para.classList.add('event-accessibility-info');
    } else if (para.innerHTML.includes('<strong>Assentos:</strong>')) {
      para.classList.add('event-seats-info');
    }
    
    if (para.innerHTML.includes('<strong>J.S. Bach</strong>') ||
        para.innerHTML.includes('<strong>W.A. Mozart</strong>') ||
        para.innerHTML.includes('<strong>L.V. Beethoven</strong>') ||
        para.innerHTML.includes('<strong>F. Chopin</strong>') ||
        para.innerHTML.includes('<strong>F. Liszt</strong>') ||
        para.innerHTML.includes('<strong>C. Debussy</strong>') ||
        para.innerHTML.includes('<strong>E. Satie</strong>')) {
      para.classList.add('composer-item');
    }
    
    if (para.textContent?.includes('sessões:') || 
        para.textContent?.match(/\d{2}\/\w+/)) {
      para.classList.add('session-date');
    }
    
    if (index === paragraphs.length - 1) {
      para.classList.add('event-note');
    }
  });
  
  return tempDiv.innerHTML;
};

const descriptionSection = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-md)',
  boxShadow: 'var(--shadow-300)',
  padding: '2.4rem',
  marginBottom: '2.4rem',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  
  "@media (max-width: 768px)": {
    padding: 'var(--spacing-lg) var(--spacing-md)',
  },
  
  "@media (max-width: 480px)": {
    padding: 'var(--spacing-md) var(--spacing-sm)',
  },
});

const sectionHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: '1.2rem',
  marginBottom: 'var(--spacing-lg)',
  position: 'relative',
});

const sectionIcon = css({
  color: 'var(--pc-700)',
  minWidth: '2rem',
  width: '2rem',
  height: '2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const sectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 700,
  color: 'var(--pc-800)',
  position: 'relative',
  padding: 0,
  margin: 0,
  lineHeight: 1.2,
  display: 'flex',
  alignItems: 'center',
  '::after': {
    content: '""',
    position: 'absolute',
    bottom: '-0.8rem',
    left: 0,
    width: '6rem',
    height: '0.3rem',
    backgroundColor: '#e7c7af',
    borderRadius: '0.3rem',
    zIndex: 1,
  },
  
  "@media (max-width: 768px)": {
    fontSize: 'var(--fs-550)',
  },
  
  "@media (max-width: 480px)": {
    fontSize: 'var(--fs-500)',
  },
});

const descriptionContent = css({
  fontSize: '1.6rem',
  lineHeight: 1.6,
  color: 'var(--g-800)',
  
  '& h2': {
    fontSize: 'var(--fs-600)',
    fontWeight: 700,
    color: 'var(--pc-700)',
    marginTop: 'var(--spacing-2xl)',
    marginBottom: 'var(--spacing-md)',
    paddingBottom: 'var(--spacing-sm)',
    borderBottom: '2px solid var(--pc-200)',
    
    '&:first-of-type': {
      marginTop: 0,
    },
    
    '@media (max-width: 768px)': {
      fontSize: 'var(--fs-550)',
    },
    
    '@media (max-width: 480px)': {
      fontSize: 'var(--fs-500)',
    },
  },
  
  '& p': {
    marginBottom: 'var(--spacing-sm)',
    fontSize: 'var(--fs-400)',
    color: 'var(--g-700)',
    
    '@media (max-width: 768px)': {
      fontSize: 'var(--fs-350)',
    },
    
    '@media (max-width: 480px)': {
      fontSize: 'var(--fs-300)',
    },
  },
  
  '& a': {
    color: 'var(--pc-600)',
    textDecoration: 'underline',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    
    '&:hover': {
      color: 'var(--pc-800)',
      textDecoration: 'none',
    },
  },
  
  '& ul, & ol': {
    marginLeft: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-md)',
  },
  
  '& li': {
    marginBottom: 'var(--spacing-xs)',
    color: 'var(--g-700)',
  },
  
  '& .event-description-heading, & .event-info-heading, & .event-program-heading': {
    color: 'var(--pc-700)',
  },
  
  '& .event-date-info, & .event-location-info, & .event-duration-info': {
    color: 'var(--g-700)',
  },
  
  '& .event-age-info, & .event-accessibility-info, & .event-seats-info': {
    color: 'var(--g-700)',
  },
  
  '& .emoji-paragraph, & .session-date, & .composer-item, & .event-note': {
    color: 'var(--g-700)',
  },
  
  '& .last-heading': {
    marginBottom: 'var(--spacing-lg)',
  },
});

export interface EventDescriptionProps {
  event: EventDetail;
}

export const EventDescription: React.FC<EventDescriptionProps> = ({ event }) => {
  const cleanedHtml = useMemo(() => {
    return cleanHtmlContent(event.detailedDescription);
  }, [event.detailedDescription]);

  return (
    <div className={descriptionSection} id="info">
      <div className={sectionHeader}>
        <IoInformationCircleOutline className={sectionIcon} size={20} />
        <h2 className={sectionTitle}>Sobre o evento</h2>
      </div>
      <div className={descriptionContent}>
        {parse(cleanedHtml)}
      </div>
    </div>
  );
};

export default EventDescription;
